import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Injectable, Injector, inject, DestroyRef } from '@angular/core';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { EntityType, ModalService, ModalType } from '@ui';
import { ReportEventConfig, ReportModalConfig } from '@app/components/report-form-config.type';
import { catchError, filter, ignoreElements, map, switchMap } from 'rxjs/operators';
import { DEFAULT_AUTOCOMPLETE_LIMIT, Languages, ReportActions, ToastService, UserRole } from '@core';
import { NewReport, Report } from '@app/core/services/reports/report.model';
import { PassDataService } from '@app/core/services/pass-data/pass-data.service';
import { ReportsStateService } from '@app/services/reports-state/reports-state.service';
import { ReportsService } from '@app/core/services/reports/reports.service';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@app/core/services/user/user.model';
import { checkUserPermissionForFinanceAndPartners } from '@app/services/helpers/helpers.helper';
import { ReportTypesEnum } from '@app/core/services/reports/reports.enum';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { UserService } from '@app/core/services/user/user.service';
import { ErrorCode } from '@core/services/toast-service/enums';
import { CalendarGetReportsParams } from '@app/pages/dashboard/models/dashboard.models';
import { AutocompleteVersion } from '@app/shared/enums/general.enums';
import { Params } from '@angular/router';
import { Client } from '@app/core/services/clients/client.model';
import { ApiService } from '@app/core/services/api/api.service';

@Injectable({
	providedIn: 'root'
})
export class NewEntryService {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	private injector = inject(Injector);
	private apiService = inject(ApiService);
	private userService = inject(UserService);
	private toastService = inject(ToastService);
	private passDataService = inject(PassDataService);
	private reportsService = inject(ReportsService);
	private translateService = inject(TranslateService);
	private reportsStateService = inject(ReportsStateService);
	private breakpointObserver = inject(BreakpointObserver);

	private modalService: ModalService | null = null;
	private readonly modalsTranslations: Record<any, string>;
	private readonly reportsTranslations: Record<any, string>;
	private readonly phoneBreakPoint: boolean;
	private readonly activeUser: User;
	private readonly autoCompletes: Record<string, (name: string | number, matterQuery?: string) => any> = {
		getClientsForFinanceReport: null,
		getNameAutocomplete: null,
		getClientById: null,
		getCasesAutocomplete: null,
		getCasesAutocompleteByClient: null,
		getFilterCasesAutoCompleteByClientId: null,
		getMatterById: null,
		getUsersAutocomplete: null,
		getEmployeesAutocomplete: null,
		getNameAutocompleteByMatterId: null,
		getLatestCases: null,
		getLatestCasesByClient: null
	};

	constructor() {
		this.modalsTranslations = this.translateService.instant('MODALS');
		this.reportsTranslations = this.translateService.instant('REPORTS');
		this.phoneBreakPoint = this.breakpointObserver.isMatched(Breakpoints.XSmall);
		this.activeUser = this.userService.getActiveUser();
		this.autoCompletes.getNameAutocomplete = (nameQuery: string) =>
			this.apiService.getClientsForAutocomplete(nameQuery, null, AutocompleteVersion.V1);
		this.autoCompletes.getCasesAutocomplete = (caseQuery: string) => {
			const params: Params = {
				active: true,
				visible: true
			};
			return this.apiService.getMattersForAutocomplete(caseQuery, AutocompleteVersion.V3, params);
		};

		this.autoCompletes.getClientById = (id: number) => this.apiService.getEntityById(id, 'clients');
		this.autoCompletes.getMatterById = (id: number) => this.apiService.getEntityById(id, 'matters');
		this.autoCompletes.getUsersAutocomplete = (query: string) =>
			this.apiService
				.getUsersForAutocomplete(query, AutocompleteVersion.V1, { roles: [UserRole.Finance, UserRole.Partner] })
				.pipe(
					map((res: any) => {
						return res.map(user => {
							return {
								...user,
								name: this.translateService.currentLang === Languages.HE ? user.alt_name : user.name
							};
						});
					})
				);

		this.autoCompletes.getEmployeesAutocomplete = (name: string) =>
			this.apiService.getUsersForAutocomplete(name, AutocompleteVersion.V1).pipe(
				map((res: any) => {
					return res.map(user => {
						return {
							...user,
							name: this.translateService.currentLang === Languages.HE ? user.alt_name : user.name
						};
					});
				})
			);

		this.autoCompletes.getNameAutocompleteByMatterId = (id: number) =>
			this.apiService.getEntities({ matters: id }, 'clients');

		this.autoCompletes.getCasesAutocompleteByClient = (id: number) => {
			const params: Params = {
				limit: DEFAULT_AUTOCOMPLETE_LIMIT,
				clients: id,
				visible: true,
				active: true,
				sort: 'user_reports'
			};
			return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		};

		this.autoCompletes.getLatestCases = (id?: number) => {
			let params: Params = {
				limit: DEFAULT_AUTOCOMPLETE_LIMIT,
				sort: 'user_reports',
				visible: true,
				active: true
			};
			if (id) {
				params.report_user = id;
			}
			return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		};

		this.autoCompletes.getLatestCasesByClient = (id: number) => {
			const params: Params = {
				clients: id,
				limit: DEFAULT_AUTOCOMPLETE_LIMIT,
				sort: 'user_reports',
				active: true,
				visible: true
			};
			return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		};

		this.autoCompletes.getFilterCasesAutoCompleteByClientId = (id: number, matterQuery: string) => {
			const params: Params = {
				clients: id,
				limit: DEFAULT_AUTOCOMPLETE_LIMIT,
				visible: true,
				active: true
			};
			return this.apiService.getMattersForAutocomplete(matterQuery, AutocompleteVersion.V3, params);
		};

		//TODO:SHOULD BE FOR FINANCE REPORT COMPONENT AUTOCOMPLETES IN FUTURE
		// this.autoCompletes.getMatterByClientForFinanceReports = (
		// 	name: string,
		// 	limit: number,
		// 	cellParams: CustomICellEditorParams
		// ): Observable<Case[]> => {
		// 	const params: RequestParams = {
		// 		clients: cellParams.data.client.id.toString(),
		// 		limit: limit.toString(),
		// 		visible: 'true',
		// 		active: 'true',
		// 		noLoader: 'true'
		// 	};
		//
		// 	if (name.trim()) {
		// 		params.name = name;
		// 	}
		//
		// 	return this.caseService.get(params);
		// };

		//TODO:SHOULD BE FOR FINANCE REPORT COMPONENT AUTOCOMPLETES IN FUTURE
		// this.autoCompletes.getEmployeesForFinanceReport = (name: string, limit: number): Observable<User[]> => {
		// 	const params: RequestParams = {
		// 		name,
		// 		limit: limit.toString(),
		// 		noLoader: 'true'
		// 	};
		//
		// 	return this.usersService.getEmployeesForFinanceReport(params);
		// };

		//TODO:SHOULD BE FOR FINANCE REPORT COMPONENT AUTOCOMPLETES IN FUTURE
		this.autoCompletes.getClientsForFinanceReport = (name: string): Observable<Client[]> => {
			return this.apiService.getClientsForAutocomplete(name, null, AutocompleteVersion.V2);
		};
		this.getModalService();
	}

	// Lazy load ModalService when needed
	private getModalService(): ModalService {
		if (!this.modalService) {
			this.modalService = this.injector.get(ModalService);
		}
		return this.modalService;
	}

	//New Report Modal
	private openNewReportConfirmCloseDialog(modalConfig: ReportModalConfig): Observable<any> {
		return this.modalService
			.openModal(ModalType.CONFIRM_CLOSE, null, null, this.modalsTranslations.CONFIRM_CLOSE_MODAL)
			.pipe(
				switchMap(confirmClose => {
					if (confirmClose) {
						return this.openNewReportModal({ ...modalConfig, afterCancel: true });
					} else {
						return EMPTY;
					}
				}),
				takeUntilDestroyed(this.destroyRef)
			);
	}

	public openNewReportModal(config?: ReportModalConfig, originEntity?: EntityType): Observable<any> {
		let modalConfig: ReportModalConfig;
		let reportRequest: Observable<any>;
		return this.modalService.openModal(ModalType.NewReport, config).pipe(
			filter(data => !!data),
			switchMap((data: ReportEventConfig) => {
				// Fill Report data if exists
				if (data.reportData) {
					modalConfig = {
						...data,
						reportData: data.reportData
					};
				}
				// Handle cancel event
				if (data.hasOwnProperty('confirmClose')) {
					if (data.confirmClose) {
						return this.openNewReportConfirmCloseDialog(modalConfig);
					} else {
						return EMPTY.pipe(ignoreElements());
					}
				}
				reportRequest = data.reportData.id
					? this.reportsService.updateReport(data.reportData.id, data.reportData)
					: this.reportsService.createNewReport(data.reportData);
				// Create new report
				return reportRequest.pipe(
					catchError(err => {
						let error: ErrorCode = err?.error?.error ? err.error.error[0] : null;
						const errorTitle: string = error ? 'ERRORS.REPORT_PERIOD_TITLE' : null;
						if (error === ErrorCode.ReportingPeriod && config.viewAsUser) {
							error = ErrorCode.ReportingPeriodViewAs;
						}
						this.toastService.error(error, errorTitle);
						throw err;
					}),
					switchMap((res: NewReport) => {
						switch (originEntity) {
							case EntityType.BILLING_LIST:
								return of({
									...res,
									isReOpen: data.reopen
								});

							case EntityType.MATTER:
								if (data.reopen) {
									modalConfig.fillFieldsAfterSaveAndNew = { client: res.client, matter: res.matter };
									modalConfig.afterSaveAndNew = true;
									return this.openNewReportModal(modalConfig, originEntity);
								} else {
									return of(res);
								}

							case EntityType.CLIENT:
								if (data.reopen) {
									modalConfig.fillFieldsAfterSaveAndNew = { client: res.client };
									modalConfig.afterSaveAndNew = true;
									return this.openNewReportModal(modalConfig, originEntity);
								} else {
									return of(res);
								}

							default:
								if (data.reopen) {
									modalConfig.afterSaveAndNew = true;
									modalConfig.isMobileView = config.isMobileView;
									this.passDataService.changeData({ [this.reportsStateService.storeValue.activeDisplay]: true });
									return this.openNewReportModal(modalConfig);
								} else {
									return of(res);
								}
						}
					})
				);
			})
		);
	}

	public handleNewReportModal(config: ReportModalConfig = {}): Subscription {
		return this.openNewReportModal(config)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.passDataService.changeData({ [this.reportsStateService.storeValue.activeDisplay]: true });
				this.toastService.success(this.translateService.instant('TOAST.REPORT_SUCCESS'));
			});
	}

	public openNewReportModalWithSelectedDay(date?: string, sendInfoField?: boolean): void {
		const selectedDate: string = this.reportsStateService.storeValue.selectedDate;
		const config: ReportModalConfig = {
			reportData: {
				date: date ?? selectedDate
			},
			reportType: this.reportsStateService.activeTab,
			viewAsUser: this.reportsStateService.storeValue.viewAsUser,
			sendInfoField,
			isMobileView: this.phoneBreakPoint
		};
		this.handleNewReportModal(config);
	}

	public openEditNewReportModal(reportData: any): void {
		const isEditHoursDisabled: boolean = !(
			checkUserPermissionForFinanceAndPartners(this.activeUser, reportData.user.id) ||
			this.activeUser.id === reportData.user.id ||
			this.activeUser.id === reportData?.reporter
		);
		const config: ReportModalConfig = {
			reportData,
			isEditMode: true,
			reportType: reportData.time_report ? ReportTypesEnum.hours : ReportTypesEnum.expenses,
			isEditHoursDisabled,
			isMobileView: this.phoneBreakPoint
		};
		this.handleNewReportModal(config);
	}

	public openEditOrViewReportModal(config: ReportModalConfig, isMobileView?: boolean): Observable<any> {
		const modalConfig: ReportModalConfig = {
			...config,
			reportType: config.reportData.time_report ? ReportTypesEnum.hours : ReportTypesEnum.expenses,
			isMobileView
		};
		return this.openNewReportModal(modalConfig);
	}

	public editReportFromCalendarDashboard(event: ReportActions): Observable<any> {
		return this.reportsService.getReportById(+event.id).pipe(
			switchMap(report => {
				const config: ReportModalConfig = {
					reportData: report,
					isEditMode: true
				};
				return this.openEditOrViewReportModal(config);
			})
		);
	}

	public duplicateEntryFromSideMonthlyStatusModal(event: ReportActions): Observable<any> {
		const reportId: number = event.id;
		const currentDate: Date = new Date(event.reportObj.date);

		return this.modalService.openModal(ModalType.DUPLICATE_ENTITY, { currentDate }).pipe(
			filter(dates => !!dates),
			switchMap((dates: string[]) => {
				return this.reportsService.duplicateReportById(reportId, dates);
			}),
			filter(totalDuplicatedEntries => !!totalDuplicatedEntries),
			catchError(err => {
				let error: ErrorCode = err?.error?.error
					? Array.isArray(err.error.error[0])
						? err.error.error[0]
						: err.error.error
					: null;
				const errorTitle: string = error
					? error === ErrorCode.ReportsDuplicate
						? 'ERRORS.REPORT_DUPLICATE_TITLE'
						: 'ERRORS.REPORT_PERIOD_TITLE'
					: null;
				this.toastService.error(error, errorTitle);
				throw err;
			}),
			takeUntilDestroyed(this.destroyRef)
		);
	}

	public deleteReportFromCalendarDashboard(report: Report): Observable<boolean> {
		const translation: Record<string, any> = this.translateService.instant('MODALS.DELETE_REPORT_MODAL');
		return this.modalService.openModal(ModalType.DEFAULT_DELETE_ITEM, report, null, translation).pipe(
			filter(reportData => !!reportData),
			switchMap(reportData => this.reportsService.delete(reportData.id)),
			map(() => true)
		);
	}

	public openExpandedReportsFromCalendarDashboard(
		currentPath: string,
		params: CalendarGetReportsParams
	): Observable<any> {
		return this.reportsService.getReportsByDateForPartnerDashboard(currentPath, params).pipe(
			switchMap(reports => {
				return this.modalService.openModal(
					ModalType.SIDE_MONTHLY_STATUS,
					{ reports },
					this.autoCompletes,
					this.reportsTranslations
				);
			})
		);
	}
}
